import React, { useRef, useEffect, useState, useCallback } from 'react';

/* ── Import sound assets (webpack asset/resource → static/media/) ── */
import laserSrc from '../assets/sounds/laser.wav';
import hitSrc from '../assets/sounds/hit.wav';
import explosionSrc from '../assets/sounds/explosion.wav';
import powerupSrc from '../assets/sounds/powerup.wav';
import launchSrc from '../assets/sounds/launch.ogg';
import enemyFireSrc from '../assets/sounds/enemy_fire.ogg';

const ENEMY_ROWS = 4;
const ENEMY_COLS = 7;
const ENEMY_GAP_X = 52;
const ENEMY_GAP_Y = 42;

/* ── Starfield ── */
const NUM_STARS = 120;
function generateStars(w, h) {
  const stars = [];
  for (let i = 0; i < NUM_STARS; i++) {
    stars.push({
      x: Math.random() * w,
      y: Math.random() * h,
      size: Math.random() * 2 + 0.5,
      speed: Math.random() * 0.3 + 0.1,
      alpha: Math.random() * 0.7 + 0.3,
      twinkleSpeed: Math.random() * 0.02 + 0.005,
      twinklePhase: Math.random() * Math.PI * 2,
    });
  }
  return stars;
}

const SOUNDS = {
  laser: laserSrc,
  hit: hitSrc,
  explosion: explosionSrc,
  powerup: powerupSrc,
  launch: launchSrc,
  enemyFire: enemyFireSrc,
};

/* ── Sound manager — AudioContext created inside user gesture (click handler) ── */
let audioCtx = null;
const bufferCache = {};

function playSound(key, enabled) {
  if (!enabled) return;
  const buf = bufferCache[key];
  if (!buf) {
    /* Buffer not decoded yet — fallback: fresh Audio() in gesture context */
    const url = SOUNDS[key];
    if (url) {
      const a = new Audio(url);
      a.volume = 0.5;
      a.play().catch(() => {});
    }
    return;
  }
  /* Web Audio path */
  if (!audioCtx) return;
  try {
    const src = audioCtx.createBufferSource();
    src.buffer = buf;
    src.connect(audioCtx.destination);
    src.start(0);
  } catch (_) {}
}

/* ── Draw player: cute cartoon ship ── */
function drawPlayer(ctx, x, y) {
  ctx.save();
  // Body
  ctx.fillStyle = '#60a5fa';
  ctx.shadowColor = '#60a5fa';
  ctx.shadowBlur = 14;
  ctx.beginPath();
  ctx.ellipse(x, y + 2, 20, 12, 0, 0, Math.PI * 2);
  ctx.fill();
  // Dome
  ctx.fillStyle = '#93c5fd';
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.ellipse(x, y - 6, 8, 10, 0, Math.PI, Math.PI * 2);
  ctx.fill();
  // Dome glass shine
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.beginPath();
  ctx.ellipse(x - 3, y - 10, 3, 5, -0.3, 0, Math.PI * 2);
  ctx.fill();
  // Window
  ctx.fillStyle = '#1e3a5f';
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.arc(x, y - 6, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.beginPath();
  ctx.arc(x - 1, y - 8, 1.5, 0, Math.PI * 2);
  ctx.fill();
  // Engine glow
  ctx.fillStyle = '#fbbf24';
  ctx.shadowColor = '#fbbf24';
  ctx.shadowBlur = 8;
  ctx.beginPath();
  ctx.ellipse(x, y + 12, 4, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/* ── Draw enemy: cartoon alien saucer ── */
function drawEnemy(ctx, x, y, type) {
  const colors = ['#ef4444', '#f97316', '#eab308', '#a855f7'];
  const bodyColor = colors[type % colors.length];
  ctx.save();
  ctx.fillStyle = bodyColor;
  ctx.shadowColor = bodyColor;
  ctx.shadowBlur = 8;
  ctx.beginPath();
  ctx.ellipse(x, y + 2, 16, 8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.ellipse(x, y - 5, 7, 7, 0, Math.PI, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#1e293b';
  ctx.beginPath();
  ctx.arc(x - 3, y - 5, 2.5, 0, Math.PI * 2);
  ctx.arc(x + 3, y - 5, 2.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.arc(x - 2, y - 6, 1, 0, Math.PI * 2);
  ctx.arc(x + 4, y - 6, 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.4)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x - 5, y - 10);
  ctx.lineTo(x - 8, y - 16);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + 5, y - 10);
  ctx.lineTo(x + 8, y - 16);
  ctx.stroke();
  ctx.fillStyle = '#fbbf24';
  ctx.shadowColor = '#fbbf24';
  ctx.shadowBlur = 4;
  ctx.beginPath();
  ctx.arc(x - 8, y - 16, 2, 0, Math.PI * 2);
  ctx.arc(x + 8, y - 16, 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawBullet(ctx, x, y) {
  const grad = ctx.createLinearGradient(x, y - 6, x, y + 6);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.3, '#fbbf24');
  grad.addColorStop(1, '#f97316');
  ctx.fillStyle = grad;
  ctx.shadowColor = '#fbbf24';
  ctx.shadowBlur = 10;
  ctx.fillRect(x - 2, y - 6, 4, 12);
}

function drawEnemyBullet(ctx, x, y) {
  ctx.fillStyle = '#f87171';
  ctx.shadowColor = '#f87171';
  ctx.shadowBlur = 8;
  ctx.beginPath();
  ctx.arc(x, y, 4, 0, Math.PI * 2);
  ctx.fill();
}

const MAX_WAVE = 20;

function createGameState(w, h, wave = 1) {
  const enemies = [];
  const wf = Math.min(wave, MAX_WAVE); // cap at wave 20
  const rows = ENEMY_ROWS + Math.floor((wf - 1) / 4);
  const cols = ENEMY_COLS + Math.floor((wf - 1) / 5);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      enemies.push({
        x: Math.max(60, (w - cols * ENEMY_GAP_X) / 2) + c * ENEMY_GAP_X,
        y: 50 + r * ENEMY_GAP_Y,
        alive: true,
        type: (r + c) % 4,
      });
    }
  }
  return {
    player: { x: w / 2, y: h - 50 },
    bullets: [],
    enemyBullets: [],
    enemies,
    wave,
    score: 0,
    dir: 1,
    speed: Math.min(0.3 + (wave - 1) * 0.08, 2.0),
    enemyFireRate: Math.min(0.012 + (wave - 1) * 0.003, 0.04),
    enemyFireCooldown: Math.max(800 - (wave - 1) * 40, 200),
    enemyBulletSpeed: Math.min(2.5 + (wave - 1) * 0.3, 6),
    gameOver: false,
    keys: { left: false, right: false, space: false },
    lastShot: 0,
    fireRate: Math.max(250 - (wave - 1) * 5, 150),
    frameId: null,
    w, h,
  };
}

const SpaceShooterGame = ({ onClose }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [score, setScore] = useState(0);
  const [bees, setBees] = useState(ENEMY_ROWS * ENEMY_COLS);
  const [phase, setPhase] = useState('menu');
  const [soundOn, setSoundOn] = useState(true);
  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn; // keep ref in sync
  const gsRef = useRef(null);

  const startGame = useCallback(() => {
    /* Create AudioContext inside user gesture — starts 'running', no block */
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    /* Fetch + decode all sounds (async) */
    Object.entries(SOUNDS).forEach(([key, url]) => {
      if (!bufferCache[key]) {
        fetch(url)
          .then(r => r.arrayBuffer())
          .then(ab => audioCtx.decodeAudioData(ab))
          .then(buf => { bufferCache[key] = buf; })
          .catch(() => {});
      }
    });
    const container = containerRef.current;
    const w = container ? container.clientWidth : 800;
    const h = container ? container.clientHeight : 600;
    gsRef.current = createGameState(w, h, 1);
    setScore(0);
    setBees(gsRef.current.enemies.length);
    setPhase('playing');
    playSound('launch', soundOnRef.current);
  }, []);

  useEffect(() => {
    if (phase !== 'playing') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const gs = gsRef.current;
    if (!gs) return;

    const container = containerRef.current;
    if (container) {
      const w = container.clientWidth;
      const h = container.clientHeight;
      canvas.width = w;
      canvas.height = h;
      gs.w = w;
      gs.h = h;
      gs.player.x = w / 2;
      gs.player.y = h - 50;
      gs.stars = gs.stars || generateStars(w, h);
    }

    const handleKey = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Space') e.preventDefault();
      if (e.key === 'ArrowLeft') gs.keys.left = true;
      if (e.key === 'ArrowRight') gs.keys.right = true;
      if (e.key === ' ' || e.key === 'Space') gs.keys.space = true;
    };
    const handleKeyUp = (e) => {
      if (e.key === 'ArrowLeft') gs.keys.left = false;
      if (e.key === 'ArrowRight') gs.keys.right = false;
      if (e.key === ' ' || e.key === 'Space') gs.keys.space = false;
    };
    window.addEventListener('keydown', handleKey);
    window.addEventListener('keyup', handleKeyUp);

    let running = true;
    let lastEnemyShot = Date.now();
    let lastFrameTime = 0;
    const TARGET_FRAME = 1000 / 60; // 16.667ms

    const loop = (timestamp) => {
      if (!running) return;

      /* ── Delta-time normalisation (target = 60 fps) ── */
      if (!lastFrameTime) lastFrameTime = timestamp;
      let dt = (timestamp - lastFrameTime) / TARGET_FRAME;
      if (dt > 3) dt = 3; // cap to avoid spiral-of-death
      lastFrameTime = timestamp;

      if (gs.gameOver) {
        playSound('explosion', soundOnRef.current);
        setPhase('over');
        return;
      }

      const p = gs.player;
      const MOVE_SPEED = 4; // px per 60fps-frame
      if (gs.keys.left) p.x = Math.max(20, p.x - MOVE_SPEED * dt);
      if (gs.keys.right) p.x = Math.min(gs.w - 20, p.x + MOVE_SPEED * dt);

      const now = Date.now();
      if (gs.keys.space && now - gs.lastShot > gs.fireRate) {
        gs.bullets.push({ x: p.x, y: p.y - 18 });
        gs.lastShot = now;
        playSound('laser', soundOnRef.current);
      }

      gs.bullets = gs.bullets.filter(b => b.y > -10).map(b => ({ ...b, y: b.y - 7 * dt }));
      gs.enemyBullets = gs.enemyBullets.filter(b => b.y < gs.h + 10).map(b => ({ ...b, y: b.y + (gs.enemyBulletSpeed || 2.5) * dt }));

      const alive = gs.enemies.filter(e => e.alive);
      if (alive.length > 0) {
        let hitEdge = false;
        alive.forEach(e => { e.x += gs.speed * gs.dir * dt; });
        alive.forEach(e => {
          if (e.x > gs.w - 60 || e.x < 60) hitEdge = true;
        });
        if (hitEdge) {
          gs.dir *= -1;
          alive.forEach(e => e.y += 8 * dt);
        }
        /* Enemy fire probability scaled by dt to stay frame-rate independent */
        const fireProb = (gs.enemyFireRate || 0.012) * dt;
        if (fireProb > 0 && Math.random() < fireProb && now - lastEnemyShot > (gs.enemyFireCooldown || 800)) {
          const shooter = alive[Math.floor(Math.random() * alive.length)];
          gs.enemyBullets.push({ x: shooter.x, y: shooter.y + 14 });
          lastEnemyShot = now;
          playSound('enemyFire', soundOnRef.current);
        }
      } else {
        const newGs = createGameState(gs.w, gs.h, gs.wave + 1);
        Object.assign(gs, newGs);
        setBees(newGs.enemies.length);
        playSound('powerup', soundOnRef.current);
      }

      gs.bullets = gs.bullets.filter(b => {
        let hit = false;
        gs.enemies.forEach(e => {
          if (!e.alive) return;
          if (Math.abs(b.x - e.x) < 14 && Math.abs(b.y - e.y) < 12) {
            e.alive = false;
            hit = true;
            gs.score += 10 * gs.wave;
            setScore(gs.score);
            setBees(gs.enemies.filter(x => x.alive).length);
            playSound('hit', soundOnRef.current);
          }
        });
        return !hit;
      });

      gs.enemyBullets.forEach(b => {
        if (Math.abs(b.x - p.x) < 18 && Math.abs(b.y - p.y) < 16) {
          gs.gameOver = true;
        }
      });

      alive.forEach(e => {
        if (e.y > gs.h - 50) gs.gameOver = true;
      });

      /* ── Deep space background + twinkling starfield ── */
      const grad = ctx.createLinearGradient(0, 0, 0, gs.h);
      grad.addColorStop(0, '#0a0a1a');
      grad.addColorStop(0.5, '#0a1628');
      grad.addColorStop(1, '#0f0a20');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, gs.w, gs.h);

      if (gs.stars) {
        gs.stars.forEach(star => {
          star.twinklePhase += star.twinkleSpeed;
          const brightness = star.alpha * (0.6 + 0.4 * Math.sin(star.twinklePhase));
          ctx.globalAlpha = brightness;
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
          ctx.fill();
        });
        ctx.globalAlpha = 1;
      }

      gs.enemies.forEach(e => { if (e.alive) drawEnemy(ctx, e.x, e.y, e.type); });
      gs.bullets.forEach(b => drawBullet(ctx, b.x, b.y));
      gs.enemyBullets.forEach(b => drawEnemyBullet(ctx, b.x, b.y));
      drawPlayer(ctx, p.x, p.y);

      gs.frameId = requestAnimationFrame(loop);
    };

    gs.frameId = requestAnimationFrame(loop);

    return () => {
      running = false;
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('keyup', handleKeyUp);
      if (gs.frameId) cancelAnimationFrame(gs.frameId);
    };
  }, [phase]);

  if (phase === 'menu') {
    return (
      <div className="absolute inset-0 z-20 flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      >
        <div className="text-center space-y-4">
          <div className="text-5xl mb-2">👽</div>
          <h2 className="text-2xl font-bold text-white/90">Space Battle</h2>
          <p className="text-white/50 text-sm max-w-xs">Destroy all alien ships!<br />← → to move, SPACE to fire</p>
          <button onClick={startGame}
            className="px-8 py-3 rounded-xl bg-indigo-500/80 hover:bg-indigo-500 text-white border border-indigo-400/40 shadow-lg transition-all hover:scale-105 text-lg"
          >
            🚀 Start Game
          </button>
          <div className="flex justify-center gap-3">
            <button onClick={() => setSoundOn(v => !v)}
              className={`px-4 py-2 rounded-lg border transition-all text-sm ${
                soundOn
                  ? 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                  : 'bg-red-900/20 border-red-800/30 text-red-400/60 hover:bg-red-900/30'
              }`}
            >
              {soundOn ? '🔊 Sound ON' : '🔇 Sound OFF'}
            </button>
            {onClose && (
              <button onClick={onClose}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 border border-white/10 transition-all text-sm"
              >
                ← Back
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'over') {
    return (
      <div className="absolute inset-0 z-20 flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      >
        <div className="text-center space-y-4">
          <div className="text-5xl mb-2">💥</div>
          <h2 className="text-2xl font-bold text-white/90">Game Over</h2>
          <p className="text-lg font-mono text-yellow-300/70">Wave {gsRef.current?.wave || 1}</p>
          <p className="text-3xl font-mono text-indigo-300">{score}</p>
          <p className="text-white/50 text-sm">points</p>
          <div className="flex gap-3 justify-center pt-2">
            <button onClick={startGame}
              className="px-6 py-2.5 rounded-xl bg-indigo-500/80 hover:bg-indigo-500 text-white border border-indigo-400/40 shadow-lg transition-all hover:scale-105"
            >
              Play Again
            </button>
            {onClose && (
              <button onClick={onClose}
                className="px-6 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all"
              >
                Exit
              </button>
            )}
          </div>
          <div className="pt-1">
            <button onClick={() => setSoundOn(v => !v)}
              className={`px-3 py-1.5 rounded-lg border transition-all text-xs ${
                soundOn
                  ? 'bg-white/10 border-white/20 text-white/50 hover:bg-white/20'
                  : 'bg-red-900/20 border-red-800/30 text-red-400/50'
              }`}
            >
              {soundOn ? '🔊 ON' : '🔇 OFF'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Playing
  return (
    <div ref={containerRef} className="absolute inset-0 z-20">
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ background: '#0a0a1a' }}
      />
      {/* HUD — top-left */}
      <div className="absolute top-6 left-6 z-30 flex items-center gap-4 text-sm font-mono">
        <span className="text-yellow-300 font-bold">WAVE {gsRef.current?.wave || 1}</span>
        <span className="text-white/30">|</span>
        <span className="text-indigo-300 font-bold">SCORE: {score}</span>
        <span className="text-white/30">|</span>
        <span className="text-orange-300">BEES: {bees}</span>
      </div>
      {/* Top-right controls */}
      <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
        <button onClick={() => setSoundOn(v => !v)}
          className="text-white/40 hover:text-white/80 text-sm px-2.5 py-1.5 bg-black/30 rounded-lg border border-white/10 transition-all hover:bg-black/50"
          title={soundOn ? 'Disable sound' : 'Enable sound'}
        >
          {soundOn ? '🔊' : '🔇'}
        </button>
        <button onClick={() => onClose && onClose()}
          className="text-white/40 hover:text-white/70 text-sm px-2.5 py-1.5 bg-black/30 rounded-lg border border-white/10 transition-all hover:bg-black/50"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default SpaceShooterGame;
